import { JsonSchema } from '@core/models/transformers.model';
import { resolveAllPropertyInformation } from '@core/utils/json-schema-utils';

import { LocaleTranslationObject } from '@shared/types/translation.type';

import {
  FILTER_OP_SHAPES,
  FieldPath,
  FilterOpName,
  LITERAL_KINDS,
  LiteralKind,
  Quantifier,
  edgePath,
  fieldPath,
  isFilterOpName,
  pathKey,
  segmentName
} from '../wire';
import {
  FieldType,
  FilterAssociation,
  FilterField,
  FilterVocabulary,
  SortableField,
  StaticVocabulary
} from './vocabulary';

type SchemaNode = Record<string, unknown>;

interface PredicateArm {
  paths: FieldPath[];
  opDef: string;
  /** The title a constant path carries, by path key; a declared metadata key has no property. */
  titles: Map<string, Partial<LocaleTranslationObject>>;
}

/** An arm whose path is open: each base name takes a key, `name[key]`. */
interface KeyedArm {
  bases: string[];
  opDef: string;
}

interface ParsedNode {
  predicates: PredicateArm[];
  keyed: KeyedArm[];
  associations: { edge: string; subDef: string }[];
}

interface AssociationMeta {
  edge: string;
  quantifiers?: Quantifier[];
  cardinality?: 'one' | 'many';
  target?: { kind: 'entity'; entityType: string } | { kind: 'junction'; table: string };
  fields?: {
    name: string;
    /** A closed value set, when the data layer serves one. */
    enum?: unknown;
    'x-filterable'?: { uidEntityType?: string };
    'x-i18n-title'?: LocaleTranslationObject;
  }[];
  sortableLeaves?: string[];
  /** A junction's own nested junctions, which have no entity schema to be listed in. */
  associations?: AssociationMeta[];
  'x-i18n-title'?: LocaleTranslationObject;
}

type FieldDetails = Pick<FilterField, 'label' | 'uidEntityType' | 'enumValues'> & {
  /** Unset when no schema property matches the path. */
  isArray?: boolean;
};
type DetailSource = (path: FieldPath) => FieldDetails;

const NO_DETAILS: FieldDetails = { label: null };

const LABEL_KIND_ORDER: LiteralKind[] = ['string', 'number', 'integer', 'bool'];
const VALUE_KIND_ORDER: LiteralKind[] = [
  'uid',
  'mass',
  'length',
  'duration',
  'date',
  'date_time',
  'number',
  'integer',
  'bool',
  'string'
];

function metasOf(owner: JsonSchema | undefined): AssociationMeta[] {
  return (owner?.['x-associations'] as AssociationMeta[] | undefined) ?? [];
}

function refName(node: unknown): string | undefined {
  const ref = (node as SchemaNode | undefined)?.['$ref'];
  return typeof ref === 'string' ? ref.split('/').pop() : undefined;
}

/** A definition written as one branch or as a `oneOf` of branches. */
function branches(node: unknown): SchemaNode[] {
  if (!node || typeof node !== 'object') return [];
  const oneOf = (node as SchemaNode)['oneOf'];
  return Array.isArray(oneOf) ? (oneOf as SchemaNode[]) : [node as SchemaNode];
}

function properties(node: SchemaNode | undefined): Record<string, SchemaNode> {
  return (node?.['properties'] as Record<string, SchemaNode> | undefined) ?? {};
}

/** Tags of an externally tagged union: bare-string variants plus single-key object variants. */
function unionTags(def: SchemaNode | undefined): string[] {
  return branches(def).flatMap((branch) => [
    ...((branch['enum'] as unknown[] | undefined) ?? []).filter(
      (v): v is string => typeof v === 'string'
    ),
    ...(typeof branch['const'] === 'string' ? [branch['const']] : []),
    ...(branch['type'] === 'object' ? Object.keys(properties(branch)) : [])
  ]);
}

/** Every definition referenced under `node`; a range body is a `oneOf` of bound variants. */
function refsUnder(node: unknown, out = new Set<string>()): Set<string> {
  if (!node || typeof node !== 'object') return out;
  const own = refName(node);
  if (own) out.add(own);
  for (const value of Object.values(node as SchemaNode)) refsUnder(value, out);
  return out;
}

/**
 * The fixed paths an arm names: listed (`enum`), alone (`const`, a declared metadata key) or as
 * `oneOf` variants of either. An open `pattern` names none; `keyedBases` reads the keyed ones.
 */
function fixedPaths(path: SchemaNode | undefined): Omit<PredicateArm, 'opDef'> {
  const paths: FieldPath[] = [];
  const titles = new Map<string, Partial<LocaleTranslationObject>>();
  for (const variant of branches(path)) {
    if (Array.isArray(variant['enum'])) paths.push(...(variant['enum'] as FieldPath[]));
    const constant = variant['const'] as FieldPath | undefined;
    if (constant?.segments) {
      paths.push(constant);
      const title = variant['x-i18n-title'] as Partial<LocaleTranslationObject> | undefined;
      if (title) titles.set(pathKey(constant), title);
    }
  }
  return { paths, titles };
}

const KEY_SUFFIX = '\\[[A-Za-z0-9_]+\\]$';
const ESCAPED_NAME = /^(?:[^\\.^$|?*+()[\]{}]|\\.)+$/;

/** The name pattern of a one-segment path whose field name is open. */
function namePattern(path: SchemaNode | undefined): unknown {
  const items = properties(path)['segments']?.['items'] as SchemaNode | undefined;
  return properties(properties(items)['field'])['name']?.['pattern'];
}

/**
 * The base names of a pattern made only of `^<name>\[[A-Za-z0-9_]+\]$` alternatives joined by `|`;
 * null for any other pattern, which names no field the vocabulary could offer.
 */
function keyedBases(pattern: unknown): string[] | null {
  if (typeof pattern !== 'string') return null;
  const names = pattern.split('|').map((alternative) => {
    const escaped =
      alternative.startsWith('^') && alternative.endsWith(KEY_SUFFIX)
        ? alternative.slice(1, -KEY_SUFFIX.length)
        : '';
    return ESCAPED_NAME.test(escaped) ? escaped.replace(/\\(.)/g, '$1') : null;
  });
  return names.every((name) => name !== null) ? (names as string[]) : null;
}

// TODO(data_layer): drop this table once the keyed pattern arm serves its key's entity type.
/**
 * The entity each keyed name's key names, which the served schema does not say. A name missing here
 * is not offered: its key picker would list the wrong entity.
 */
const KEY_ENTITY_TYPES: Readonly<Record<string, string>> = {
  '#assoc:kpi.average_capacities': 'CAPACITY_RESOURCE',
  '#assoc:kpi.average_capacity_percentages': 'CAPACITY_RESOURCE',
  '#assoc:kpi.loading_rates': 'CAPACITY_RESOURCE',
  '#assoc:kpi.peak_capacities': 'CAPACITY_RESOURCE',
  '#assoc:kpi.peak_capacity_percentages': 'CAPACITY_RESOURCE',
  '#assoc:kpi.total_load_capacities': 'CAPACITY_RESOURCE',
  '#assoc:kpi.nb_routes_per_vehicle': 'VEHICLE'
};

function keyEntityType(base: string): string | undefined {
  const entityType = Object.prototype.hasOwnProperty.call(KEY_ENTITY_TYPES, base)
    ? KEY_ENTITY_TYPES[base]
    : undefined;
  if (!entityType)
    console.warn(`[path filter] No key entity known for "${base}"; not offered`);
  return entityType;
}

function readNode(defs: Record<string, SchemaNode>, name: string): ParsedNode {
  const node = defs[name];
  const props = properties(node);
  const predicates = branches(props['predicate']).flatMap((arm) => {
    const armProps = properties(arm);
    const { paths, titles } = fixedPaths(armProps['path']);
    const opDef = refName(armProps['op']);
    return paths.length > 0 && opDef ? [{ paths, opDef, titles }] : [];
  });
  const keyed = branches(props['predicate']).flatMap((arm) => {
    const armProps = properties(arm);
    const bases = keyedBases(namePattern(armProps['path']));
    const opDef = refName(armProps['op']);
    return bases && opDef ? [{ bases, opDef }] : [];
  });
  const associations = branches(props['association']).flatMap((arm) => {
    const armProps = properties(arm);
    const edge = armProps['edge']?.['const'];
    const subDef = refName(armProps['filter']);
    return typeof edge === 'string' && subDef ? [{ edge, subDef }] : [];
  });
  return { predicates, keyed, associations };
}

/** The editor picks its control from the first kind, so the order is a contract, not the served order. */
function orderKinds(op: FilterOpName, kinds: LiteralKind[]): LiteralKind[] {
  const precedence = FILTER_OP_SHAPES[op].startsWith('label_')
    ? LABEL_KIND_ORDER
    : VALUE_KIND_ORDER;
  const rank = (kind: LiteralKind) => {
    const index = precedence.indexOf(kind);
    return index === -1 ? precedence.length : index;
  };
  return kinds
    .map((kind, served) => ({ kind, served }))
    .sort((a, b) => rank(a.kind) - rank(b.kind) || a.served - b.served)
    .map(({ kind }) => kind);
}

function operatorsOf(
  defs: Record<string, SchemaNode>,
  opDef: string
): Map<FilterOpName, LiteralKind[]> {
  const out = new Map<FilterOpName, LiteralKind[]>();
  for (const branch of branches(defs[opDef])) {
    for (const unit of (branch['enum'] as unknown[] | undefined) ?? []) {
      if (typeof unit === 'string' && isFilterOpName(unit)) out.set(unit, []);
    }
    if (branch['type'] !== 'object') continue;
    for (const [tag, body] of Object.entries(properties(branch))) {
      if (!isFilterOpName(tag)) continue;
      const kinds = [...refsUnder(body)]
        .flatMap((def) => unionTags(defs[def]))
        .filter((kind): kind is LiteralKind =>
          (LITERAL_KINDS as readonly string[]).includes(kind)
        );
      out.set(tag, orderKinds(tag, [...new Set(kinds)]));
    }
  }
  return narrowUntyped(out);
}

/** Kinds one value of a field may be written as; `null` belongs to none. */
const KIND_FAMILY: Partial<Record<LiteralKind, string>> = {
  uid: 'text',
  string: 'text',
  date: 'date',
  date_time: 'date',
  number: 'number',
  integer: 'number',
  duration: 'duration',
  mass: 'mass',
  length: 'length',
  bool: 'bool'
};

function familiesOf(kinds: LiteralKind[]): Set<string> {
  return new Set(kinds.flatMap((kind) => KIND_FAMILY[kind] ?? []));
}

/**
 * GBE cannot type a metadata key, so it serves the key's comparisons with every literal kind:
 * `equals` on a due date takes a bool or a uid alike, and the editor would offer the wrong control.
 * A typed field takes at most a number beside its own kind (a mass range), so three families mark a
 * key as untyped. Its type still shows in the rest of what it serves: only a date offers
 * `relative_date`, and the set operators of a uid key take uids and strings only. Each operator
 * spanning several families keeps the one that evidence names; without evidence, all stay as
 * served. Label operators take a label's value, whatever its kind, and are never narrowed.
 */
function narrowUntyped(
  operators: Map<FilterOpName, LiteralKind[]>
): Map<FilterOpName, LiteralKind[]> {
  const valueKinds = [...operators].filter(
    ([op]) => !FILTER_OP_SHAPES[op].startsWith('label_')
  );
  if (!valueKinds.some(([, kinds]) => familiesOf(kinds).size > 2)) return operators;
  const mixed = (kinds: LiteralKind[]) => familiesOf(kinds).size > 1;
  let family: string | undefined;
  if (operators.has('relative_date')) {
    family = 'date';
  } else {
    const typed = new Set(
      valueKinds
        .filter(([, kinds]) => !mixed(kinds))
        .flatMap(([, kinds]) => [...familiesOf(kinds)])
    );
    if (typed.size === 1) family = [...typed][0];
  }
  if (family === undefined) return operators;
  const narrowed = new Map(operators);
  for (const [op, kinds] of valueKinds) {
    const kept = kinds.filter((kind) => (KIND_FAMILY[kind] ?? family) === family);
    if (mixed(kinds) && kept.some((kind) => KIND_FAMILY[kind] === family))
      narrowed.set(op, kept);
  }
  return narrowed;
}

interface ClosedValues {
  values: string[];
  titles: Record<string, Partial<LocaleTranslationObject>>;
}

/**
 * The string constants an op definition's operands are limited to, in served order, each with its
 * title. An operand whose `string` variants are not all constants (an open `{type: string}` among
 * them) limits nothing.
 */
function closedValues(defs: Record<string, SchemaNode>, opDef: string): ClosedValues {
  const closed: ClosedValues = { values: [], titles: {} };
  for (const def of refsUnder(defs[opDef])) {
    const text = properties(defs[def])['string'];
    const variants = (text?.['oneOf'] ?? text?.['anyOf']) as SchemaNode[] | undefined;
    const closedSet = variants?.every((variant) => typeof variant['const'] === 'string');
    if (!variants?.length || !closedSet) continue;
    for (const variant of variants) {
      const value = variant['const'] as string;
      if (closed.values.includes(value)) continue;
      closed.values.push(value);
      const title = variant['x-i18n-title'] as Partial<LocaleTranslationObject> | undefined;
      if (title) closed.titles[value] = title;
    }
  }
  return closed;
}

function inferFieldType(
  operators: Map<FilterOpName, LiteralKind[]>,
  isArray: boolean
): FieldType {
  const names = [...operators.keys()];
  if (names.some((name) => name === 'has_tag' || name === 'has_any_tag')) return 'TAGS';
  if (names.some((name) => name === 'has_label' || name.startsWith('label_')))
    return 'LABELS';
  const kinds = new Set([...operators.values()].flat());
  if (kinds.has('mass')) return 'MASS';
  if (kinds.has('length')) return 'LENGTH';
  if (kinds.has('duration')) return 'DURATION';
  if (kinds.has('date')) return 'DATE';
  if (kinds.has('date_time')) return 'DATE_TIME';
  if (kinds.has('bool')) return 'BOOL';
  if (kinds.has('uid')) return isArray ? 'UID_LIST' : 'UID';
  if (kinds.has('number')) return 'NUMBER';
  if (kinds.has('integer')) return 'INTEGER';
  return isArray ? 'LIST' : 'TEXT';
}

function stringValues(values: unknown): string[] | undefined {
  return Array.isArray(values) && values.every((v) => typeof v === 'string')
    ? values
    : undefined;
}

function labelOf(property: SchemaNode): Partial<LocaleTranslationObject> | null {
  const titles = property['x-i18n-title'] as Partial<LocaleTranslationObject> | undefined;
  const title = typeof property['title'] === 'string' ? property['title'] : undefined;
  if (!titles) return title ? { en: title } : null;
  return titles.en || !title ? titles : { ...titles, en: title };
}

/** Details of a path from the entity properties: matched by column (`x-filterable.dbColName`) or name. */
function propertyDetails(schema: JsonSchema): DetailSource {
  return (path) => {
    let props: Record<string, JsonSchema> = schema.properties ?? {};
    let property: JsonSchema | undefined;
    for (const segment of path.segments) {
      const name = segmentName(segment);
      property = Object.entries(props).find(
        ([key, candidate]) =>
          key === name || candidate?.['x-filterable']?.dbColName === name
      )?.[1];
      if (!property) return NO_DETAILS;
      const resolved = (resolveAllPropertyInformation(property, schema).data ??
        property) as JsonSchema;
      props = resolved.properties ?? resolved.items?.properties ?? {};
    }
    if (!property) return NO_DETAILS;
    const resolved = (resolveAllPropertyInformation(property, schema).data ??
      property) as JsonSchema;
    const filterable = property['x-filterable'] as
      | { uidEntityType?: string; availableValues?: unknown }
      | undefined;
    return {
      label: labelOf(property),
      isArray: resolved.type === 'array',
      uidEntityType:
        filterable?.uidEntityType ??
        (resolved['x-reference'] as string | undefined) ??
        (resolved.items?.['x-reference'] as string | undefined),
      enumValues:
        stringValues(resolved.enum) ??
        stringValues(resolved.items?.enum) ??
        stringValues(filterable?.availableValues)
    };
  };
}

function junctionDetails(meta: AssociationMeta): DetailSource {
  return (path) => {
    const entry = meta.fields?.find(
      (field) => field.name === segmentName(path.segments[0])
    );
    if (!entry) return NO_DETAILS;
    const enumValues = stringValues(entry.enum);
    return {
      label: entry['x-i18n-title'] ?? null,
      uidEntityType: entry['x-filterable']?.uidEntityType,
      ...(enumValues ? { enumValues } : {})
    };
  };
}

function buildFields(
  node: ParsedNode,
  defs: Record<string, SchemaNode>,
  details: DetailSource
): FilterField[] {
  const fields: FilterField[] = node.predicates.flatMap(({ paths, opDef, titles }) => {
    const operators = operatorsOf(defs, opDef);
    // Without a matching property, only the list-only operators reveal an array.
    const listOnly = operators.has('contains_all') || operators.has('not_contains_all');
    const served = closedValues(defs, opDef);
    return paths.map((path) => {
      const found = details(path);
      const enumValues =
        found.enumValues ?? (served.values.length ? served.values : undefined);
      return {
        key: pathKey(path),
        path,
        type: inferFieldType(operators, found.isArray ?? listOnly),
        operators: [...operators.keys()],
        literalKinds: Object.fromEntries(operators),
        label: titles.get(pathKey(path)) ?? found.label,
        ...(found.uidEntityType ? { uidEntityType: found.uidEntityType } : {}),
        ...(enumValues ? { enumValues } : {}),
        ...(Object.keys(served.titles).length ? { enumLabels: served.titles } : {})
      };
    });
  });
  // A keyed name is often also served bare (a whole KPI map): both readings make one field.
  for (const { bases, opDef } of node.keyed) {
    const operators = operatorsOf(defs, opDef);
    for (const base of bases) {
      const by = keyEntityType(base);
      if (!by) continue;
      const path = fieldPath(base);
      const key = pathKey(path);
      const keyed = {
        by,
        operators: [...operators.keys()],
        literalKinds: Object.fromEntries(operators)
      };
      const type = inferFieldType(operators, false);
      const index = fields.findIndex((field) => field.key === key);
      if (index === -1) {
        const { label } = details(path);
        fields.push({ key, path, label, type, operators: [], literalKinds: {}, keyed });
      } else {
        fields[index] = { ...fields[index], type, keyed };
      }
    }
  }
  return fields;
}

function sortableFields(
  schema: JsonSchema,
  associations: FilterAssociation[]
): SortableField[] {
  const base = Object.entries(schema.properties ?? {}).flatMap(([name, property]) => {
    const sortable = property?.['x-sortable'] as
      | boolean
      | { dbColName?: string }
      | undefined;
    if (!sortable) return [];
    const field =
      typeof sortable === 'object' && sortable.dbColName ? sortable.dbColName : name;
    return [{ field, label: labelOf(property) }];
  });
  const related = associations.flatMap((association) =>
    association.sortableLeaves.map((leaf) => ({
      field: leaf,
      label: null,
      association: { edge: association.edge, cardinality: association.cardinality }
    }))
  );
  return [...base, ...related];
}

interface ReadEdge {
  association: FilterAssociation;
  fields: FilterField[];
  /** The edge's sub-filter node, which lists the associations allowed inside it. */
  sub: ParsedNode;
  /** Metadata of the associations its sub-filter may hold. */
  innerMetas: AssociationMeta[];
}

function readEdge(
  arm: { edge: string; subDef: string },
  metas: AssociationMeta[],
  defs: Record<string, SchemaNode>,
  schemaFor: (entityType: string) => JsonSchema | undefined
): ReadEdge {
  const meta = metas.find((candidate) => candidate.edge === arm.edge) ?? { edge: arm.edge };
  const target = meta.target?.kind === 'entity' ? meta.target.entityType : null;
  const targetSchema = target ? schemaFor(target) : undefined;
  const sub = readNode(defs, arm.subDef);
  const details =
    meta.target?.kind === 'junction'
      ? junctionDetails(meta)
      : propertyDetails(targetSchema ?? {});
  return {
    association: {
      edge: arm.edge,
      label: meta['x-i18n-title'] ?? null,
      quantifiers: meta.quantifiers ?? ['any', 'all'],
      cardinality: meta.cardinality ?? 'many',
      targetEntityType: target,
      sortableLeaves: meta.sortableLeaves ?? []
    },
    fields: buildFields(sub, defs, details),
    sub,
    innerMetas:
      meta.target?.kind === 'junction' ? (meta.associations ?? []) : metasOf(targetSchema)
  };
}

/**
 * Edges come from `x-filter-schema`: the root node's association arms, and the arms of each of
 * their sub-nodes (the data layer nests a junction inside an entity edge, e.g. a route's
 * `actions` → `movements`, or inside a junction, e.g. a job's `echelons` → `alternatives`). An
 * edge's metadata comes from the `x-associations` of the entity whose filter holds it: the root
 * entity's for a root edge, the outer edge's target's for one nested in an entity edge, and the
 * outer junction entry's own `associations` for one nested in a junction.
 */
export function dataSelectionVocabulary(
  entityType: string,
  schema: JsonSchema,
  schemaFor: (entityType: string) => JsonSchema | undefined
): FilterVocabulary | null {
  const filterSchema = schema['x-filter-schema'] as SchemaNode | undefined;
  if (!filterSchema) return null;
  const defs = (filterSchema['$defs'] as Record<string, SchemaNode> | undefined) ?? {};
  const root = readNode(defs, refName(filterSchema) ?? 'Node');

  const associations: FilterAssociation[] = [];
  const scoped = new Map<string, FilterField[]>();
  const nested = new Map<string, FilterAssociation[]>();
  for (const arm of root.associations) {
    const outer = readEdge(arm, metasOf(schema), defs, schemaFor);
    associations.push(outer.association);
    scoped.set(arm.edge, outer.fields);
    const inner = outer.sub.associations.map((innerArm) =>
      readEdge(innerArm, outer.innerMetas, defs, schemaFor)
    );
    if (inner.length === 0) continue;
    nested.set(
      arm.edge,
      inner.map((edge) => edge.association)
    );
    for (const edge of inner) {
      scoped.set(edgePath(arm.edge, edge.association.edge), edge.fields);
    }
  }

  return new StaticVocabulary({
    entityType,
    allowsSelfRef: false,
    allowsRelativeDate: true,
    root: buildFields(root, defs, propertyDetails(schema)),
    scoped,
    associations,
    nested,
    selfFields: [],
    sortable: sortableFields(schema, associations)
  });
}
